import React, { useState } from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { useToast } from '../../hooks/use-toast';
import { countries, serviceOptions, hearAboutOptions } from '../../mock';
import { PhoneCall, Loader2 } from 'lucide-react';
import { submitLead } from '../../lib/api';
import { useI18n } from '../../i18n';

const ConsultationForm = () => {
  const { toast } = useToast();
  const { t, lang } = useI18n();
  const [form, setForm] = useState({
    name: '', email: '', country: 'IN', mobile: '', service: '', hear: '',
  });
  const [loading, setLoading] = useState(false);

  const dial = countries.find((c) => c.code === form.country)?.dial || '+91';

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.mobile) {
      toast({ title: t('form.validation'), description: t('form.validationDesc') });
      return;
    }
    setLoading(true);
    try {
      await submitLead({ ...form, dial, lang });
      toast({ title: t('form.success'), description: t('form.successDesc') });
      setForm({ name: '', email: '', country: 'IN', mobile: '', service: '', hear: '' });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map((d) => d.msg).join(', ') : detail || '';
      toast({ title: t('form.error'), description: msg });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative rounded-2xl border border-white/10 bg-white shadow-2xl p-6 sm:p-7">
      <div className="absolute -top-3 left-6 bg-orange-500 text-white text-xs font-semibold px-3 py-1 rounded-full flex items-center gap-1">
        <PhoneCall className="h-3.5 w-3.5" /> {t('form.ribbon')}
      </div>
      <h3 className="text-lg font-bold text-slate-900">{t('form.title')}</h3>
      <p className="text-xs text-slate-500 mt-1">{t('form.subtitle')}</p>

      <form onSubmit={submit} className="mt-4 space-y-3">
        <div>
          <Label className="text-xs text-slate-700">{t('form.name')}</Label>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={t('form.namePh')} className="h-11 mt-1" />
        </div>
        <div>
          <Label className="text-xs text-slate-700">{t('form.email')}</Label>
          <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder={t('form.emailPh')} className="h-11 mt-1" />
        </div>
        <div>
          <Label className="text-xs text-slate-700">{t('form.mobile')}</Label>
          <div className="mt-1 flex gap-2">
            <Select value={form.country} onValueChange={(v) => setForm({ ...form, country: v })}>
              <SelectTrigger className="w-28 h-11"><SelectValue /></SelectTrigger>
              <SelectContent>
                {countries.map((c) => (<SelectItem key={c.code} value={c.code}>{c.code} {c.dial}</SelectItem>))}
              </SelectContent>
            </Select>
            <Input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value.replace(/[^0-9]/g, '') })} placeholder={t('form.mobilePh')} className="h-11 flex-1" />
          </div>
        </div>

        <div>
          <Label className="text-xs text-slate-700">{t('form.selectService')}</Label>
          <Select value={form.service} onValueChange={(v) => setForm({ ...form, service: v })}>
            <SelectTrigger className="h-11 mt-1"><SelectValue placeholder={t('form.servicePh')} /></SelectTrigger>
            <SelectContent>
              {serviceOptions.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label className="text-xs text-slate-700">{t('form.hear')}</Label>
          <Select value={form.hear} onValueChange={(v) => setForm({ ...form, hear: v })}>
            <SelectTrigger className="h-11 mt-1"><SelectValue placeholder={t('form.hearPh')} /></SelectTrigger>
            <SelectContent>
              {hearAboutOptions.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>

        <Button type="submit" disabled={loading} className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white font-semibold">
          {loading ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> {t('common.submitting')}</>) : t('form.submit')}
        </Button>

        <p className="text-[11px] text-slate-500 text-center pt-1">
          {t('common.trustedWorldwide')} · {t('common.isoCertified')}
        </p>
      </form>
    </div>
  );
};

export default ConsultationForm;
